import { Badge, Button, Dialog, DialogBody, DialogContent, DialogTrigger, Icon, Text } from '@civilgrid/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type AgencyHistoryChange,
  fetchProjectAgencyHistory,
  type ProjectHistoryEvent,
  projectAgencyHistoryQueryKey,
} from '~/api/projectAgencyHistory';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import {
  agencyLabel,
  CHANGE_TYPE_META,
  changedFieldLines,
  formatEventSummary,
  formatEventTimestamp,
  formatReason,
} from './agencyHistoryView';

// Admin-only, read-only view of the project event log. Diagnostic surface — it
// answers "why did this project's agency set change?", so it shows one row per update, newest
// first, and only expands to per-agency detail on demand.

export default function AgencyHistoryDialog({ projectId }: { projectId: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: projectAgencyHistoryQueryKey(projectId),
    queryFn: () => fetchProjectAgencyHistory(projectId),
    refetchOnWindowFocus: false,
    // Diagnostic-only: don't spend a request until an admin actually opens the popup.
    enabled: isOpen,
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) capturePosthogEvent(POSTHOG_EVENTS.projectAgencyHistoryOpened, { project_id: projectId });
  };

  const events = data?.events ?? [];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="utilities-top-nav-agency-history">
          [INTERNAL] History
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Agency History"
        className="z-[110] w-auto min-w-min max-w-[75vw] w-[42rem] max-w-[90vw]"
        overlayClassName="z-[100]"
        data-testid="utilities-top-nav-agency-history-dialog"
      >
        <DialogBody className="text-sm">
          <div className="flex flex-col gap-2" data-testid="agency-history-dialog">
            {isLoading ? (
              <Text className="text-sm text-grey-60" data-testid="agency-history-loading">
                Loading agency history…
              </Text>
            ) : isError ? (
              <Text className="text-sm text-red-60" data-testid="agency-history-error">
                Couldn't load agency history. Please try again.
              </Text>
            ) : events.length === 0 ? (
              <Text className="text-sm text-grey-60" data-testid="agency-history-empty">
                No agency history yet.
              </Text>
            ) : (
              events.map((event) => <EventRow key={event.id} event={event} />)
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function EventRow({ event }: { event: ProjectHistoryEvent }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded border border-grey-30" data-testid={`agency-history-event-${event.id}`}>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        // The counts render as three separate chips, which a screen reader would read as bare
        // numbers — label the row with the whole summary instead.
        aria-label={`${formatEventTimestamp(event.createdAt)} ${formatReason(event.reason)} ${formatEventSummary(event.counts)}`}
        data-testid={`agency-history-event-toggle-${event.id}`}
      >
        <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} size={14} aria-hidden />
        <span className="text-sm font-medium">{formatEventTimestamp(event.createdAt)}</span>
        <Badge color="blue">{formatReason(event.reason)}</Badge>
        <span className="ml-auto flex items-center gap-1">
          <Badge color="green">+{event.counts.added}</Badge>
          <Badge color="red">−{event.counts.removed}</Badge>
          <Badge color="yellow">~{event.counts.modified}</Badge>
        </span>
      </button>

      {isExpanded && (
        <ul
          className="flex flex-col gap-1 border-t border-grey-30 px-3 py-2"
          data-testid={`agency-history-changes-${event.id}`}
        >
          {event.changes.map((change) => (
            <ChangeRow key={`${change.agencyId}-${change.changeType}`} change={change} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ChangeRow({ change }: { change: AgencyHistoryChange }) {
  const meta = CHANGE_TYPE_META[change.changeType];
  // What a 'modified' change actually touched — the new value, since capture stores no prior one.
  const detail = changedFieldLines(change);

  return (
    <li className="flex flex-col gap-0.5" data-testid={`agency-history-change-${change.agencyId}`}>
      <div className="flex items-center gap-2 text-sm">
        <Badge color={meta.color}>
          {meta.symbol} {change.changeType}
        </Badge>
        <span className="truncate">{agencyLabel(change)}</span>
        <span className="ml-auto shrink-0 text-xs text-grey-60">
          {change.assetsInProjectArea ? '✓ assets' : '— no assets'}
        </span>
      </div>
      {detail.length > 0 && (
        <ul className="pl-2 text-xs text-grey-60" data-testid={`agency-history-change-detail-${change.agencyId}`}>
          {detail.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </li>
  );
}
